import { apiClient } from './client';
import {
  ApiResponse,
  PaginatedResponse,
  LoanProduct,
  LoanApplication,
  Loan,
  PaymentSchedule,
  Payment,
  UserStats,
  AdminStats,
  PaymentMethod,
} from '../types';

export * from './auth';

export const loanProductsApi = {
  getAll: async (): Promise<ApiResponse<{ products: LoanProduct[] }>> => {
    return apiClient.get('/loan-products');
  },

  getById: async (id: string): Promise<ApiResponse<{ product: LoanProduct }>> => {
    return apiClient.get(`/loan-products/${id}`);
  },

  calculate: async (
    product_id: string,
    amount: number,
    term_months: number
  ): Promise<
    ApiResponse<{
      calculation: {
        principal: number;
        interest_rate: number;
        term_months: number;
        monthly_payment: number;
        total_repayment: number;
        total_interest: number;
        processing_fee: number;
        total_cost: number;
      };
    }>
  > => {
    return apiClient.post('/loan-products/calculate', {
      product_id,
      amount,
      term_months,
    });
  },

  getDocumentRequirements: async (
    product_id: string
  ): Promise<
    ApiResponse<{
      product_type: string;
      product_name: string;
      requirements: Array<{
        documentType: string;
        label: string;
        required: boolean;
        description: string;
        acceptedFormats: string[];
      }>;
    }>
  > => {
    return apiClient.get(`/loan-products/${product_id}/document-requirements`);
  },
};

export const loanApplicationsApi = {
  create: async (data: {
    product_id: string;
    amount_requested: number;
    loan_term_months: number;
    purpose?: string;
    monthly_income?: number;
    [key: string]: any;
  }): Promise<ApiResponse<{ application: LoanApplication }>> => {
    return apiClient.post('/loan-applications', data);
  },

  getAll: async (
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<LoanApplication>> => {
    return apiClient.get(`/loan-applications?page=${page}&limit=${limit}`);
  },

  getById: async (id: string): Promise<ApiResponse<{ application: any }>> => {
    return apiClient.get(`/loan-applications/${id}`);
  },

  update: async (id: string, data: any): Promise<ApiResponse> => {
    return apiClient.put(`/loan-applications/${id}`, data);
  },

  submit: async (id: string): Promise<ApiResponse> => {
    return apiClient.post(`/loan-applications/${id}/submit`);
  },

  delete: async (id: string): Promise<ApiResponse> => {
    return apiClient.delete(`/loan-applications/${id}`);
  },
};

export const loansApi = {
  getAll: async (page: number = 1, limit: number = 10): Promise<PaginatedResponse<Loan>> => {
    return apiClient.get(`/loans?page=${page}&limit=${limit}`);
  },

  getById: async (id: string): Promise<ApiResponse<{ loan: Loan }>> => {
    return apiClient.get(`/loans/${id}`);
  },

  getPaymentSchedule: async (
    id: string
  ): Promise<ApiResponse<{ schedule: PaymentSchedule[] }>> => {
    return apiClient.get(`/loans/${id}/schedule`);
  },

  getPayments: async (id: string): Promise<ApiResponse<{ payments: Payment[] }>> => {
    return apiClient.get(`/loans/${id}/payments`);
  },

  calculateEarlySettlement: async (id: string): Promise<ApiResponse<{ settlement: any }>> => {
    return apiClient.get(`/loans/${id}/early-settlement`);
  },
};

export const paymentsApi = {
  // Initialize payment with gateway
  initialize: async (data: {
    loan_id: string;
    amount: number;
    email: string;
    payment_method?: string;
    channels?: string[];
  }): Promise<ApiResponse<any>> => {
    return apiClient.post('/payments/initialize', data);
  },

  // Verify payment with gateway
  verify: async (reference: string): Promise<ApiResponse<any>> => {
    return apiClient.get(`/payments/verify/${reference}`);
  },

  // Record manual payment (with proof upload)
  record: async (data: {
    loan_id: string;
    amount: number;
    payment_method: PaymentMethod;
    transaction_reference?: string;
    notes?: string;
    bank_name?: string;
    payment_date?: string;
    payment_proof?: File;
  }): Promise<ApiResponse<{ payment: Payment }>> => {
    // If payment_proof is provided, use FormData for file upload
    if (data.payment_proof) {
      const formData = new FormData();
      formData.append('loan_id', data.loan_id);
      formData.append('amount', data.amount.toString());
      formData.append('payment_method', data.payment_method);
      if (data.transaction_reference) formData.append('transaction_reference', data.transaction_reference);
      if (data.notes) formData.append('notes', data.notes);
      if (data.bank_name) formData.append('bank_name', data.bank_name);
      if (data.payment_date) formData.append('payment_date', data.payment_date);
      formData.append('payment_proof', data.payment_proof);

      return apiClient.upload('/payments', formData);
    }

    // Otherwise, use regular JSON request
    return apiClient.post('/payments', data);
  },

  getById: async (id: string): Promise<ApiResponse<{ payment: Payment }>> => {
    return apiClient.get(`/payments/${id}`);
  },
};

export const documentsApi = {
  upload: async (
    file: File,
    document_type: string,
    application_id?: string
  ): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', document_type);
    if (application_id) {
      formData.append('application_id', application_id);
    }
    return apiClient.upload('/documents', formData);
  },

  uploadMultiple: async (formData: FormData): Promise<ApiResponse> => {
    return apiClient.upload('/documents/bulk', formData);
  },

  getAll: async (application_id?: string): Promise<ApiResponse<{ documents: any[] }>> => {
    const url = application_id
      ? `/documents?application_id=${application_id}`
      : '/documents';
    return apiClient.get(url);
  },

  delete: async (id: string): Promise<ApiResponse> => {
    return apiClient.delete(`/documents/${id}`);
  },
};

// Customer-specific API
export const customerApi = {
  getDocuments: async (): Promise<ApiResponse<{ documents: any[] }>> => {
    return apiClient.get('/customer/documents');
  },

  uploadDocuments: async (formData: FormData): Promise<ApiResponse> => {
    return apiClient.upload('/customer/documents', formData);
  },

  deleteDocument: async (id: string): Promise<ApiResponse> => {
    return apiClient.delete(`/customer/documents/${id}`);
  },

  getProfile: async (): Promise<ApiResponse<any>> => {
    return apiClient.get('/customer/profile');
  },

  updateProfile: async (data: any): Promise<ApiResponse> => {
    return apiClient.put('/customer/profile', data);
  },

  verifyBVN: async (bvn: string): Promise<ApiResponse<{ verification: any }>> => {
    return apiClient.post('/customer/verify-bvn', { bvn });
  },

  verifyAccount: async (account_number: string, bank_code: string): Promise<ApiResponse<{ account_name: string }>> => {
    return apiClient.post('/customer/verify-account', { account_number, bank_code });
  },
};

export const userApi = {
  updateProfile: async (data: any): Promise<ApiResponse> => {
    return apiClient.put('/users/profile', data);
  },

  getStats: async (): Promise<ApiResponse<{ stats: UserStats }>> => {
    return apiClient.get('/users/stats');
  },
};

export const adminApi = {
  getStats: async (): Promise<ApiResponse<{ stats: AdminStats }>> => {
    return apiClient.get('/admin/stats');
  },

  getApplications: async (
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<PaginatedResponse<any>> => {
    const url = status
      ? `/admin/applications?page=${page}&limit=${limit}&status=${status}`
      : `/admin/applications?page=${page}&limit=${limit}`;
    return apiClient.get(url);
  },

  getApplicationById: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.get(`/admin/applications/${id}`);
  },

  approveApplication: async (
    id: string,
    approval_notes?: string
  ): Promise<ApiResponse> => {
    return apiClient.put(`/admin/applications/${id}/approve`, { approval_notes });
  },

  declineApplication: async (id: string, decline_reason: string): Promise<ApiResponse> => {
    return apiClient.put(`/admin/applications/${id}/decline`, { decline_reason });
  },

  disburseLoan: async (
    id: string,
    interest_rate: number,
    disbursement_date?: string
  ): Promise<ApiResponse> => {
    return apiClient.post(`/admin/applications/${id}/disburse`, {
      interest_rate,
      disbursement_date,
    });
  },

  getLoans: async (
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<PaginatedResponse<Loan>> => {
    const url = status
      ? `/admin/loans?page=${page}&limit=${limit}&status=${status}`
      : `/admin/loans?page=${page}&limit=${limit}`;
    return apiClient.get(url);
  },

  verifyDocument: async (
    id: string,
    is_verified: boolean,
    verification_notes?: string
  ): Promise<ApiResponse> => {
    return apiClient.put(`/admin/documents/${id}/verify`, {
      is_verified,
      verification_notes,
    });
  },

  getPendingPayments: async (
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<any>> => {
    return apiClient.get(`/admin/payments/pending?page=${page}&limit=${limit}`);
  },

  getPaymentById: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.get(`/admin/payments/${id}`);
  },

  verifyPayment: async (
    id: string,
    is_verified: boolean,
    verification_notes?: string
  ): Promise<ApiResponse> => {
    return apiClient.put(`/admin/payments/${id}/verify`, {
      is_verified,
      verification_notes,
    });
  },

  getGatewaySettings: async (): Promise<ApiResponse<any>> => {
    return apiClient.get('/admin/gateway-settings');
  },

  getActiveGateway: async (): Promise<ApiResponse<any>> => {
    return apiClient.get('/admin/gateway-settings/active');
  },

  updateGatewaySettings: async (
    gatewayName: string,
    settings: any
  ): Promise<ApiResponse> => {
    return apiClient.put(`/admin/gateway-settings/${gatewayName}`, settings);
  },

  testGatewayConnection: async (gatewayName: string): Promise<ApiResponse> => {
    return apiClient.post(`/admin/gateway-settings/${gatewayName}/test`, {});
  },

  requestDocuments: async (
    applicationId: string,
    requested_documents: string[],
    request_message?: string
  ): Promise<ApiResponse> => {
    return apiClient.post(`/admin/applications/${applicationId}/request-documents`, {
      requested_documents,
      request_message,
    });
  },

  getDocumentRequests: async (applicationId: string): Promise<ApiResponse<{ requests: any[] }>> => {
    return apiClient.get(`/admin/applications/${applicationId}/document-requests`);
  },

  cancelDocumentRequest: async (requestId: string): Promise<ApiResponse> => {
    return apiClient.delete(`/admin/document-requests/${requestId}`);
  },

  // NIBSS Settings
  getNIBSSSettings: async (): Promise<ApiResponse<{ settings: any }>> => {
    return apiClient.get('/admin/nibss-settings');
  },

  updateNIBSSSettings: async (settings: any): Promise<ApiResponse> => {
    return apiClient.put('/admin/nibss-settings', settings);
  },

  testNIBSSConnection: async (): Promise<ApiResponse> => {
    return apiClient.post('/admin/nibss-settings/test', {});
  },

  // Customer Management
  getCustomers: async (
    page: number = 1,
    limit: number = 20,
    search?: string,
    status?: string
  ): Promise<PaginatedResponse<any>> => {
    let url = `/admin/customers?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    return apiClient.get(url);
  },

  updateCustomer: async (userId: string, data: any): Promise<ApiResponse> => {
    return apiClient.put(`/admin/customers/${userId}`, data);
  },

  updateCustomerStatus: async (userId: string, status: string): Promise<ApiResponse> => {
    return apiClient.put(`/admin/customers/${userId}/status`, { status });
  },

  suspendCustomer: async (userId: string, reason?: string): Promise<ApiResponse> => {
    return apiClient.put(`/admin/customers/${userId}/suspend`, { reason });
  },

  unsuspendCustomer: async (userId: string): Promise<ApiResponse> => {
    return apiClient.put(`/admin/customers/${userId}/unsuspend`, {});
  },

  getCustomer360View: async (userId: string): Promise<ApiResponse<any>> => {
    return apiClient.get(`/admin/customers/${userId}/360`);
  },

  // Scoring Rules
  getScoringRules: async (category?: string): Promise<ApiResponse<{ rules: any[] }>> => {
    const url = category ? `/admin/scoring-rules?category=${category}` : '/admin/scoring-rules';
    return apiClient.get(url);
  },

  getScoringStatistics: async (): Promise<ApiResponse<any>> => {
    return apiClient.get('/admin/scoring-rules/statistics');
  },

  createScoringRule: async (data: any): Promise<ApiResponse> => {
    return apiClient.post('/admin/scoring-rules', data);
  },

  updateScoringRule: async (ruleId: string, data: any): Promise<ApiResponse> => {
    return apiClient.put(`/admin/scoring-rules/${ruleId}`, data);
  },

  deleteScoringRule: async (ruleId: string): Promise<ApiResponse> => {
    return apiClient.delete(`/admin/scoring-rules/${ruleId}`);
  },

  // Guarantor Management
  getGuarantorsForApplication: async (applicationId: string): Promise<ApiResponse<{ guarantors: any[] }>> => {
    return apiClient.get(`/guarantor/application/${applicationId}`);
  },

  resendGuarantorRequest: async (requestId: string): Promise<ApiResponse> => {
    return apiClient.post(`/guarantor/requests/${requestId}/resend`, {});
  },
};

export const notificationsApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    channel?: string;
  }): Promise<PaginatedResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.channel) queryParams.append('channel', params.channel);

    return apiClient.get(`/notifications?${queryParams.toString()}`);
  },

  getUnreadCount: async (): Promise<ApiResponse<{ unread_count: number }>> => {
    return apiClient.get('/notifications/unread-count');
  },

  markAsRead: async (id: string): Promise<ApiResponse> => {
    return apiClient.put(`/notifications/${id}/read`, {});
  },

  markAllAsRead: async (): Promise<ApiResponse> => {
    return apiClient.put('/notifications/read-all', {});
  },
};
